import React, { useEffect, useMemo, useRef, useState } from 'react';
import Select, { components } from 'react-select';
import { addAssigneeStyles } from '../../../../style';

// "Assign to" for an OATRx Flow step: All / Pharmacists / Assistants tabs above a checkbox dropdown
// that stays open while picking several users. `staff` items: { id, name, role: 'pharmacist' | 'assistant' }.

// Staff names are stored as typed (e.g. "umang k mishra"); show each word capitalized.
const capitalizeName = (name) => (name || '').replace(/\S+/g, (word) => word.charAt(0).toUpperCase() + word.slice(1));

const TABS = [
	{ key: 'all', label: 'All' },
	{ key: 'pharmacist', label: 'Pharmacists' },
	{ key: 'assistant', label: 'Assistants' },
];

const pickerStyles = {
	...addAssigneeStyles,
	control: (provided, state) => ({
		...addAssigneeStyles.control(provided, state),
		minHeight: '34px',
		borderColor: '#d1d5db',
	}),
	option: (provided, state) => ({
		...provided,
		backgroundColor: state.isFocused ? '#eff6ff' : 'white',
		color: '#374151',
		padding: '6px 10px',
		cursor: 'pointer',
	}),
	placeholder: (provided, state) => ({
		...provided,
		color: state.selectProps.selectedCount > 0 ? '#1f2937' : provided.color,
		// Long name lists end with "…" instead of wrapping.
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		maxWidth: '100%',
	}),
	menuPortal: (provided) => ({ ...provided, zIndex: 9999 }),
};

const CheckboxOption = (props) => {
	const isLocked = props.selectProps.lockedUserId != null
		&& Number(props.data.value) === Number(props.selectProps.lockedUserId);

	return (
		<components.Option {...props} isDisabled={isLocked || props.isDisabled}>
			<span className={`flex items-center gap-2 ${isLocked ? 'opacity-60' : ''}`}>
				<input
					type="checkbox"
					checked={props.isSelected}
					disabled={isLocked}
					readOnly
					tabIndex={-1}
					className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-0"
				/>
				<span className="truncate">{props.label}</span>
				{isLocked && <span className="ml-auto shrink-0 text-[10px] text-gray-400">you</span>}
			</span>
		</components.Option>
	);
};

// "Select all" sits at the top of the list as its own checkbox row.
const MenuListWithSelectAll = (props) => {
	const { selectAllLabel, allVisibleSelected, onToggleSelectAll } = props.selectProps;

	return (
		<components.MenuList {...props}>
			{onToggleSelectAll && (
				<div
					role="button"
					tabIndex={-1}
					onMouseDown={(event) => { event.preventDefault(); event.stopPropagation(); }}
					onClick={onToggleSelectAll}
					// Same look as a normal option row (see pickerStyles.option).
					style={{ padding: '6px 10px', color: '#374151' }}
					className="flex cursor-pointer items-center gap-2 hover:bg-blue-50"
				>
					<input
						type="checkbox"
						checked={allVisibleSelected}
						readOnly
						tabIndex={-1}
						className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-0"
					/>
					<span className="truncate">{selectAllLabel}</span>
				</div>
			)}
			{props.children}
		</components.MenuList>
	);
};

// lockedUserId: this user stays on the step and cannot be unticked here (an assignee handing the step
// on keeps it themselves; the creator can still take it off them).
// excludedUserId: this user is never offered (the step's creator, who cannot be an assignee of their
// own step), so "All" means everyone who can actually be assigned.
const FlowAssigneePicker = ({ staff = [], value = [], onChange, disabled = false, loading = false, lockedUserId = null, excludedUserId = null }) => {
	// null = no tab chosen yet (nothing selected, no dropdown). 'all' assigns everyone without a dropdown;
	// 'pharmacist' / 'assistant' show the checkbox dropdown filtered to that role.
	const [tab, setTab] = useState(null);
	// Set once the user picks a tab or changes the selection, or once a saved selection has been
	// restored below, so the picker never reopens a tab the user has just closed.
	const tabSettledRef = useRef(false);

	const allOptions = useMemo(() => staff
		.filter((member) => excludedUserId == null || Number(member.id) !== Number(excludedUserId))
		.map((member) => ({
			value: Number(member.id),
			label: capitalizeName(member.name),
			role: member.role,
		})), [staff, excludedUserId]);

	const visibleOptions = tab === 'pharmacist' || tab === 'assistant'
		? allOptions.filter((option) => option.role === tab)
		: [];
	const selectedIds = new Set(value.map((user) => Number(user.id)));
	const selectedOptions = value.map((user) => ({
		value: Number(user.id),
		label: capitalizeName(user.name) || `User ${user.id}`,
	}));

	const emit = (options) => {
		tabSettledRef.current = true;
		const next = options.map((option) => ({ id: Number(option.value), name: option.label }));
		// Put the locked user back if the change would have dropped them.
		if (lockedUserId != null && !next.some((user) => Number(user.id) === Number(lockedUserId))) {
			const locked = value.find((user) => Number(user.id) === Number(lockedUserId))
				|| allOptions.find((option) => Number(option.value) === Number(lockedUserId));
			if (locked) next.push({ id: Number(locked.id ?? locked.value), name: locked.name ?? locked.label });
		}
		onChange(next);
	};

	// Add every visible user, keeping users already selected from other tabs.
	const selectVisible = () => {
		const merged = [...selectedOptions];
		visibleOptions.forEach((option) => {
			if (!selectedIds.has(option.value)) merged.push(option);
		});
		emit(merged);
	};

	const deselectVisible = () => {
		const visibleIds = new Set(visibleOptions.map((option) => option.value));
		emit(selectedOptions.filter((option) => !visibleIds.has(option.value)));
	};

	const allSelected = allOptions.length > 0 && allOptions.every((option) => selectedIds.has(option.value));
	const visibleAllSelected = visibleOptions.length > 0 && visibleOptions.every((option) => selectedIds.has(option.value));

	// Editing a step that is already assigned to everyone opens on "All".
	useEffect(() => {
		if (tab === null && allSelected) setTab('all');
	}, [tab, allSelected]);

	// How many of the selected users sit under each role tab.
	const selectedCountByRole = allOptions.reduce((counts, option) => (
		selectedIds.has(option.value) ? { ...counts, [option.role]: (counts[option.role] || 0) + 1 } : counts
	), {});
	const selectedPharmacists = selectedCountByRole.pharmacist || 0;
	const selectedAssistants = selectedCountByRole.assistant || 0;

	// Editing a step that is already assigned to some users opens on the tab holding them
	// (Pharmacists first when both roles are assigned), so the saved selection is visible.
	useEffect(() => {
		if (tabSettledRef.current || tab !== null || allSelected) return;
		const savedTab = selectedPharmacists > 0 ? 'pharmacist' : (selectedAssistants > 0 ? 'assistant' : null);
		if (!savedTab) return;
		tabSettledRef.current = true;
		setTab(savedTab);
	}, [tab, allSelected, selectedPharmacists, selectedAssistants]);

	const handleTabClick = (key) => {
		tabSettledRef.current = true;
		if (key === 'all') {
			// Toggle: assign everyone in the background, or clear and go back to nothing selected.
			if (tab === 'all') {
				setTab(null);
				emit([]);
			} else {
				setTab('all');
				emit(allOptions);
			}
			return;
		}

		// Leaving "All" for a role starts that role's own selection.
		if (tab === 'all') emit([]);
		setTab(tab === key ? null : key);
	};

	const count = value.length;
	// First two selected names, then "+N more" (e.g. "Anant Mrar, Harjas Gill +3 more").
	const selectedSummary = count <= 2
		? selectedOptions.map((option) => option.label).join(', ')
		: `${selectedOptions.slice(0, 2).map((option) => option.label).join(', ')} +${count - 2} more`;
	const showDropdown = tab === 'pharmacist' || tab === 'assistant';
	const tabLabel = TABS.find((item) => item.key === tab)?.label.toLowerCase();

	return (
		<div>
			<div className="mb-1 text-[11px] font-medium text-gray-600">Assign to</div>
			<div className="mb-1.5 flex flex-wrap gap-1.5">
				{TABS.map((item) => {
					// A role tab that holds assigned users is marked with their count, also while the other tab is open.
					const roleCount = tab === 'all' ? 0 : (selectedCountByRole[item.key] || 0);
					const tabClass = tab === item.key
						? 'border border-blue-300 bg-blue-100 text-blue-800'
						: roleCount > 0
							? 'border border-blue-300 bg-white text-blue-700 hover:bg-blue-50'
							: 'border border-gray-300 bg-white text-gray-600 hover:border-blue-300 hover:text-blue-700';

					return (
						<button
							key={item.key}
							type="button"
							disabled={disabled}
							onClick={() => handleTabClick(item.key)}
							className={`inline-flex h-7 items-center justify-center rounded-md px-2.5 text-xs font-semibold transition-colors ${tabClass} ${disabled ? 'cursor-not-allowed opacity-60' : ''}`}
						>
							{item.label}
							{roleCount > 0 && (
								<span className="ml-1.5 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-semibold text-white">
									{roleCount}
								</span>
							)}
						</button>
					);
				})}
			</div>

			{showDropdown && (
			<Select
				isMulti
				isSearchable
				closeMenuOnSelect={false}
				hideSelectedOptions={false}
				controlShouldRenderValue={false}
				isClearable={false}
				backspaceRemovesValue={false}
				options={visibleOptions}
				value={selectedOptions}
				onChange={(options) => emit(options || [])}
				isDisabled={disabled}
				isLoading={loading}
				selectedCount={count}
				placeholder={count > 0 ? selectedSummary : `Search ${tabLabel}...`}
				noOptionsMessage={() => (staff.length === 0 ? 'No staff found' : `No ${tabLabel} found`)}
				components={{ Option: CheckboxOption, MenuList: MenuListWithSelectAll }}
				selectAllLabel="All"
				lockedUserId={lockedUserId}
				allVisibleSelected={visibleAllSelected}
				onToggleSelectAll={visibleOptions.length > 0 ? () => (visibleAllSelected ? deselectVisible() : selectVisible()) : null}
				aria-label="Assign this step to users"
				className="react-select-container text-xs"
				classNamePrefix="react-select"
				styles={pickerStyles}
				menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
				menuPosition="fixed"
			/>
			)}

			{/* With no tab open there is no dropdown either, so the assigned users are named here. */}
			{tab === null && count > 0 && (
				<div className="mt-1.5 truncate text-[11px] font-medium text-gray-600" title={selectedOptions.map((option) => option.label).join(', ')}>
					{count} selected: {selectedSummary}
				</div>
			)}

			{/* "All" has no dropdown, so it keeps a small summary with a way to undo it. */}
			{tab === 'all' && count > 0 && (
				<div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[11px] font-medium">
					<span className="inline-flex h-5 items-center rounded-full bg-green-50 px-2 text-green-700 ring-1 ring-inset ring-green-200">
						All {count} selected
					</span>
					<button
						type="button"
						disabled={disabled}
						onClick={() => { emit([]); setTab(null); }}
						className="inline-flex h-5 items-center rounded-full bg-gray-50 px-2 text-gray-600 ring-1 ring-inset ring-gray-200 hover:bg-gray-100 disabled:opacity-60"
					>
						× Clear
					</button>
				</div>
			)}
		</div>
	);
};

export default FlowAssigneePicker;
